import { Link } from 'react-router-dom'
import { useLocale } from '../../i18n/LocaleContext'
import { navRoutes, getTermsPath, getPrivacyPath, getCookiePath, getFaqPath } from '../../utils/routes'
import { track } from '../../lib/analytics'

export default function Footer() {
  const { locale, t } = useLocale()
  const routes = navRoutes[locale]

  return (
    <footer className="bg-secondary text-white">
      <div className="container-max px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {/* Column 1: Brand */}
          <div>
            <Link to={`/${locale}`} className="inline-block mb-4" aria-label="Magic Harghita Adventures">
              <img
                src="/logo.jpg"
                alt="Magic Harghita Adventures"
                width={220}
                height={124}
                className="w-44 h-auto rounded-xl"
              />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed">{t.footer.description}</p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-heading font-medium text-base mb-4">{t.footer.quickLinks}</h3>
            <nav className="flex flex-col gap-2">
              {routes.map((route) => (
                <Link
                  key={route.path}
                  to={route.path}
                  className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-600 hover:text-primary hover:decoration-primary transition-colors"
                >
                  {t.nav[route.labelKey]}
                </Link>
              ))}
              <Link
                to={getTermsPath(locale)}
                className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-600 hover:text-primary hover:decoration-primary transition-colors"
              >
                {t.footer.terms}
              </Link>
              <Link
                to={getPrivacyPath(locale)}
                className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-600 hover:text-primary hover:decoration-primary transition-colors"
              >
                {t.footer.privacy}
              </Link>
              <Link
                to={getCookiePath(locale)}
                className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-600 hover:text-primary hover:decoration-primary transition-colors"
              >
                {t.footer.cookies}
              </Link>
              <Link
                to={getFaqPath(locale)}
                className="text-sm text-gray-400 underline underline-offset-4 decoration-gray-600 hover:text-primary hover:decoration-primary transition-colors"
              >
                {t.footer.faq}
              </Link>
            </nav>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h3 className="font-heading font-medium text-base mb-4">{t.footer.contactTitle}</h3>
            <div className="flex flex-col gap-2 text-sm text-gray-400">
              <a
                href={`mailto:${t.contact.info.email}`}
                className="hover:text-primary transition-colors"
              >
                {t.contact.info.email}
              </a>
              <a
                href={`tel:${t.contact.info.phone.replace(/\s/g, '')}`}
                onClick={() => track('phone_click', { location: 'footer' })}
                className="hover:text-primary transition-colors"
              >
                {t.contact.info.phone}
              </a>
              <p>{t.contact.info.address}</p>
            </div>
          </div>
        </div>

        {/* ANPC — SAL & SOL (mandatory for RO online businesses, ANPC Order 449/2022).
            Official 250×50 banners; link to the ANPC SAL page and the EU ODR/SOL platform. */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-wrap items-center justify-center gap-4">
          <a
            href="https://anpc.ro/ce-este-sal/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ANPC — Soluționarea Alternativă a Litigiilor (SAL)"
          >
            <img
              src="/anpc-sal.webp"
              alt="ANPC SAL — Soluționarea Alternativă a Litigiilor"
              width={250}
              height={50}
              loading="lazy"
              className="h-[50px] w-auto"
            />
          </a>
          <a
            href="https://ec.europa.eu/consumers/odr"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Soluționarea Online a Litigiilor (SOL / ODR)"
          >
            <img
              src="/anpc-sol.webp"
              alt="ANPC SOL — Soluționarea Online a Litigiilor"
              width={250}
              height={50}
              loading="lazy"
              className="h-[50px] w-auto"
            />
          </a>
        </div>

        {/* NETOPIA trust badge (payment security seal). The official mny.ro
            widget builds itself on DOMContentLoaded, which has already fired by
            the time this SPA mounts, so we render its badge image directly —
            white variant for the dark footer, linking to Netopia. */}
        <div className="mt-8 flex justify-center">
          <a
            href="https://netopia-payments.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="NETOPIA Payments"
            title="NETOPIA Payments"
          >
            <img
              src="https://mny.ro/np-white-0.svg"
              alt="NETOPIA Payments"
              width={250}
              height={50}
              loading="lazy"
              className="h-[50px] w-auto"
            />
          </a>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">{t.footer.copyright}</p>
          <p className="text-sm text-gray-600">
            Powered by{' '}
            <a
              href="https://splitagency.eu"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-primary transition-colors"
            >
              Split Agency
            </a>
          </p>
        </div>
      </div>
    </footer>
  )
}
